import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { Check, Clock } from "lucide-react";
import { useLocale } from "@/lib/locale";

export default function Upgrade() {
    const nav = useNavigate();
    const loc = useLocation();
    const { t } = useLocale();
    const [currentTier, setCurrentTier] = useState("free");
    const [packages, setPackages] = useState({});
    const [busy, setBusy] = useState("");
    const [err, setErr] = useState("");

    useEffect(() => {
        Promise.all([
            api.get("/plan/limits").then(({ data }) => setCurrentTier(data.plan_tier)).catch(() => {}),
            api.get("/billing/packages").then(({ data }) => setPackages(data)).catch(() => {}),
        ]);
    }, []);

    const TIERS = [
        {
            key: "free",
            level: t("upgrade.tier1_level"),
            name: t("upgrade.tier1_name"),
            tagline: t("upgrade.tier1_tagline"),
            emotional: t("upgrade.tier1_emotional"),
            depth: t("upgrade.tier1_depth"),
            features: [
                t("upgrade.feat.p1"),
                t("upgrade.feat.msg30"),
                t("upgrade.feat.transcr"),
                t("upgrade.feat.uns"),
            ],
            now: [
                t("upgrade.tier1_now"),
                t("upgrade.tier1_now2"),
                t("upgrade.tier1_now3"),
            ],
            unavailable: [t("upgrade.feat.voice"), t("upgrade.feat.photo"), t("upgrade.feat.unlim")],
            canBuy: false,
        },
        {
            key: "echo",
            level: t("upgrade.tier2_level"),
            name: t("upgrade.tier2_name"),
            tagline: t("upgrade.tier2_tagline"),
            emotional: t("upgrade.tier2_emotional"),
            depth: t("upgrade.tier2_depth"),
            features: [
                t("upgrade.feat.p3"),
                t("upgrade.feat.unlim"),
                t("upgrade.feat.voice_soon"),
                t("upgrade.feat.uns_on"),
                t("upgrade.feat.priority"),
            ],
            now: [
                t("upgrade.tier2_now"),
                t("upgrade.tier2_now2"),
                t("upgrade.tier2_now3"),
            ],
            unavailable: [t("upgrade.feat.photo")],
            canBuy: true,
            mostChosen: true,
        },
        {
            key: "legacy",
            level: t("upgrade.tier3_level"),
            name: t("upgrade.tier3_name"),
            tagline: t("upgrade.tier3_tagline"),
            emotional: t("upgrade.tier3_emotional"),
            depth: t("upgrade.tier3_depth"),
            features: [
                t("upgrade.feat.p10"),
                t("upgrade.feat.unlim"),
                t("upgrade.feat.voice_soon"),
                t("upgrade.feat.photo_soon"),
                t("upgrade.feat.future"),
            ],
            now: [
                t("upgrade.tier3_now"),
                t("upgrade.tier3_now2"),
                t("upgrade.tier3_now3"),
            ],
            unavailable: [],
            canBuy: true,
        },
    ];

    const upgrade = async (plan) => {
        setErr("");
        setBusy(plan);
        try {
            const { data } = await api.post("/billing/checkout", {
                plan,
                origin: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            setErr(e?.response?.data?.detail || t("wiz.err_generic"));
            setBusy("");
        }
    };

    const formatPrice = (n) => `€${Number(n).toFixed(2).replace(".", ",")}`;

    return (
        <div className="grain min-h-screen candle-halo">
            <Header />
            <main className="max-w-6xl mx-auto px-6 md:px-12 pt-14 pb-24 animate-fade-in">

                <div className="text-center max-w-3xl mx-auto mb-20" data-testid="upgrade-ceremony">
                    <div className="flex justify-center mb-10 animate-fade-in-slow">
                        <BrandMark size={76} ceremonial />
                    </div>
                    <p className="label-eyebrow text-ink-gold mb-6" data-testid="upgrade-eyebrow">
                        {t("upgrade.eyebrow")}
                    </p>
                    <h1 className="h-serif text-4xl md:text-6xl leading-[1.05]" data-testid="upgrade-hook">
                        {t("upgrade.hook_a")} <span className="italic text-ink-gold">{t("upgrade.hook_i")}</span>
                    </h1>
                    <p className="mt-8 text-ink-textMid text-lg leading-relaxed max-w-xl mx-auto whitespace-pre-line">
                        {t("upgrade.body")}
                    </p>
                </div>

                <div
                    className="max-w-3xl mx-auto mb-14 flex items-center justify-center gap-3 py-3 px-5 border border-ink-gold/40 bg-ink-gold/5 rounded-sm text-ink-gold"
                    data-testid="upgrade-urgency"
                >
                    <Clock size={15} />
                    <p className="text-sm tracking-wide">{t("upgrade.urgency")}</p>
                </div>

                <div className="text-center mb-14" data-testid="upgrade-decision-tension">
                    <p className="font-serif italic text-2xl md:text-3xl text-ink-textHigh leading-snug max-w-2xl mx-auto">
                        {t("upgrade.tension_a")} <span className="text-ink-gold">{t("upgrade.tension_b")}</span>
                    </p>
                </div>

                {loc.state?.reason && (
                    <p className="mt-4 mb-10 text-center text-ink-gold font-serif italic text-xl" data-testid="upgrade-reason">
                        {loc.state.reason}
                    </p>
                )}

                <p className="label-eyebrow text-center mb-8">{t("upgrade.tiers_label")}</p>
                <div className="grid md:grid-cols-3 gap-6 md:gap-8 items-stretch">
                    {TIERS.map((tier) => {
                        const isCurrent = currentTier === tier.key;
                        const pkg = packages[tier.key];
                        const amount = pkg?.amount;
                        const listPrice = pkg?.list_price;
                        const highlighted = tier.mostChosen;
                        return (
                            <div
                                key={tier.key}
                                data-testid={`tier-${tier.key}`}
                                className={`relative border p-8 md:p-10 rounded-sm transition-all duration-500 flex flex-col ${
                                    highlighted
                                        ? "border-ink-gold bg-ink-surfaceUp md:scale-[1.04] md:shadow-[0_0_70px_-14px_rgba(200,161,101,0.4)]"
                                        : "border-ink-border bg-ink-surface"
                                }`}
                            >
                                {highlighted && (
                                    <p className="label-eyebrow text-ink-gold absolute -top-3 left-8 bg-ink-deep px-3" data-testid="tier-echo-most-chosen">
                                        {t("upgrade.most_chosen")}
                                    </p>
                                )}

                                <p className="label-eyebrow text-ink-textLow">{tier.level}</p>
                                <p className="font-serif italic text-ink-textMid text-base mt-3">{tier.tagline}</p>
                                <h2 className="font-serif text-4xl text-ink-textHigh mt-1">{tier.name}</h2>
                                <p className="text-ink-gold text-sm mt-2 tracking-wide">{tier.depth}</p>

                                <p className="mt-7" data-testid={`tier-${tier.key}-price`}>
                                    {tier.key === "free" ? (
                                        <>
                                            <span className="font-serif text-5xl text-ink-textHigh">€0</span>
                                            <span className="text-ink-textLow text-sm ml-2">{t("upgrade.forever")}</span>
                                        </>
                                    ) : amount ? (
                                        <>
                                            {listPrice && Number(listPrice) > Number(amount) && (
                                                <span className="text-ink-textLow line-through mr-3 text-xl align-middle">
                                                    {formatPrice(listPrice)}
                                                </span>
                                            )}
                                            <span className="font-serif text-5xl text-ink-textHigh align-middle">{formatPrice(amount)}</span>
                                            <span className="text-ink-textLow text-sm ml-2 block mt-2">{t("upgrade.monthly")}</span>
                                        </>
                                    ) : (
                                        <span className="text-ink-textLow">…</span>
                                    )}
                                </p>

                                <p className="mt-6 font-serif italic text-ink-textHigh leading-snug text-[1.05rem]">
                                    {tier.emotional}
                                </p>

                                {/* "Cosa puoi fare SUBITO" — concrete benefit bullets */}
                                <div className="mt-7 border border-ink-gold/25 bg-ink-gold/[0.03] rounded-sm p-5" data-testid={`tier-${tier.key}-now`}>
                                    <p className="label-eyebrow text-ink-gold mb-3">
                                        {t("upgrade.now_label")}
                                    </p>
                                    <ul className="space-y-2">
                                        {tier.now.map((n) => (
                                            <li key={n} className="flex items-start gap-2.5 text-ink-textHigh text-[0.98rem] leading-snug">
                                                <span className="text-ink-gold shrink-0 mt-[2px]">→</span>
                                                <span>{n}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <ul className="mt-7 space-y-3">
                                    {tier.features.map((f) => (
                                        <li key={f} className="flex items-start gap-3 text-ink-textHigh">
                                            <Check size={16} className="text-ink-gold shrink-0 mt-1" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                    {tier.unavailable.map((f) => (
                                        <li key={f} className="flex items-start gap-3 text-ink-textLow text-sm">
                                            <span className="w-4 h-[1px] bg-ink-textLow mt-3 shrink-0 block"></span>
                                            <span className="line-through">{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-10 pt-6 border-t border-ink-border/60">
                                    {isCurrent ? (
                                        <p className="label-eyebrow text-ink-gold">{t("upgrade.current")}</p>
                                    ) : tier.canBuy ? (
                                        <button
                                            onClick={() => upgrade(tier.key)}
                                            disabled={busy === tier.key}
                                            data-testid={`upgrade-btn-${tier.key}`}
                                            className={`${highlighted ? "btn-primary-glow" : "btn-primary"} w-full`}
                                        >
                                            {busy === tier.key ? t("upgrade.busy") : highlighted ? t("upgrade.buy_echo") : `${t("upgrade.buy_generic")} ${tier.name}`}
                                        </button>
                                    ) : (
                                        <button onClick={() => nav("/dashboard")} className="btn-ghost w-full">
                                            {t("upgrade.back_dash")}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {err && <p className="mt-8 text-ink-danger font-serif italic" data-testid="upgrade-error">{err}</p>}

                <div className="mt-24 border-t border-ink-border pt-12 max-w-3xl mx-auto text-center">
                    <div className="flex justify-center mb-6 opacity-70">
                        <BrandMark size={26} breathing={false} />
                    </div>
                    <p className="font-serif italic text-ink-textMid leading-relaxed text-lg">
                        {t("upgrade.closing")}
                    </p>
                </div>

                <p className="mt-12 text-xs text-ink-textLow italic text-center">
                    {t("upgrade.smallprint")}
                </p>
            </main>
        </div>
    );
}
